import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import Navigation from "@/components/Navigation";
import MobileTabBar from "@/components/MobileTabBar";
import ScrollManager from "@/components/ScrollManager";
import BackToTop from "@/components/BackToTop";
import GuidedTour from "@/components/GuidedTour";
import InstallPrompt from "@/components/InstallPrompt";
import IOSInstallHint from "@/components/IOSInstallHint";
import ThresholdRitual from "@/components/ThresholdRitual";
import Landing from "@/pages/Landing";
import Archive from "@/pages/Archive";
import Traditions from "@/pages/Traditions";
import TraditionDetail from "@/pages/TraditionDetail";
import ConceptDetail from "@/pages/ConceptDetail";
import Themes from "@/pages/Themes";
import ThemeDetail from "@/pages/ThemeDetail";
import Oracle from "@/pages/Oracle";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Weekly from "@/pages/Weekly";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import PublicProfile from "@/pages/PublicProfile";
import Inbox from "@/pages/Inbox";
import Messages from "@/pages/Messages";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Footer from "@/components/Footer";
import OracleSharedConversation from "@/pages/OracleSharedConversation";
import ShelfPage from "@/pages/ShelfPage";
import SavedItems from "@/pages/SavedItems";
import Collections from "@/pages/Collections";
import ComingSoon from "@/pages/ComingSoon";
import InviteEntry, { hasInviteAccess } from "@/pages/InviteEntry";
import ShortLinkResolver from "@/pages/ShortLinkResolver";
import DeleteAccount from "@/pages/DeleteAccount";
import Press from "@/pages/Press";
import Methodology from "@/pages/Methodology";

const SPLASH_MODE = process.env.REACT_APP_LAUNCH_MODE === "splash";

const TOASTER_OPTIONS = {
  className: "mono !text-xs !uppercase !tracking-[0.18em] !bg-secondary !border !border-border !text-foreground/85 !rounded-none",
};

function AppRouter() {
  const location = useLocation();
  // Process emergent OAuth callback synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Splash-only mode for pre-launch: render the coming-soon page chrome-free.
  // The full app routes remain accessible by direct URL (so admins can still
  // reach /admin, /archive, etc.) — only `/` is replaced.
  // Invited seekers (those who came in via /enter) bypass the splash entirely.
  const invited = hasInviteAccess();
  const showSplashAtRoot = SPLASH_MODE && !invited;
  const onSplashRoute = location.pathname === "/coming-soon" || (showSplashAtRoot && location.pathname === "/");

  if (onSplashRoute) {
    return (
      <Routes>
        <Route path="/" element={<ComingSoon />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/enter" element={<InviteEntry />} />
      </Routes>
    );
  }

  return (
    <>
      <ScrollManager />
      <Navigation />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/enter" element={<InviteEntry />} />
        <Route path="/archive" element={<Archive />} />
        <Route path="/traditions" element={<Traditions />} />
        <Route path="/traditions/:name" element={<TraditionDetail />} />
        <Route path="/concept/:id" element={<ConceptDetail />} />
        <Route path="/themes" element={<Themes />} />
        <Route path="/theme/:id" element={<ThemeDetail />} />
        <Route path="/oracle" element={<Oracle />} />
        <Route path="/oracle/c/:slug" element={<OracleSharedConversation />} />
        <Route path="/weekly" element={<Weekly />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/:handleOrId" element={<Messages />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/u/:userId" element={<PublicProfile />} />
        <Route path="/shelf/:owner/:slug" element={<ShelfPage />} />
        <Route path="/saves" element={<SavedItems />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/press" element={<Press />} />
        <Route path="/media" element={<Press />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/c/:shortId" element={<ShortLinkResolver />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
      <GuidedTour />
      <ThresholdRitual />
      <InstallPrompt />
      <IOSInstallHint />
      <BackToTop />
      <MobileTabBar />
    </>
  );
}

function NotFound() {
  return (
    <div className="max-w-2xl mx-auto px-6 py-40 text-center" data-testid="not-found">
      <div className="label mb-6">404</div>
      <h1 className="serif text-5xl text-foreground">This passage is sealed.</h1>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="bottom-right" toastOptions={TOASTER_OPTIONS} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
